import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { customerApi } from "../Apis/Api";
import { apiCall } from "../Apis/apiCall";
import CustomSelect from "../components/common/CustomSelect";
import LoadingComponent from "../components/ui/LoadingComponent";
import Breadcrumb from "../components/Breadcrumb";
import CustomPhoneInput from "../components/common/CustomPhoneInput";
import PlateNumberComponent from "../components/common/PlateNumberComponent";
import { CancelButtonForm, UpdateButtonForm } from "../components/ui/Button";
import { X } from "lucide-react";

// Color Options
const colorOptions = [
  { value: "white", label: "White", hex: "#FFFFFF" },
  { value: "black", label: "Black", hex: "#000000" },
  { value: "silver", label: "Silver", hex: "#C0C0C0" },
  { value: "gray", label: "Gray", hex: "#808080" },
  { value: "blue", label: "Blue", hex: "#2196F3" },
  { value: "red", label: "Red", hex: "#F44336" },
  { value: "brown", label: "Brown", hex: "#795548" },
  { value: "green", label: "Green", hex: "#4CAF50" },
  { value: "yellow", label: "Yellow", hex: "#FFEB3B" },
  { value: "gold", label: "Gold", hex: "#FFD700" },
  { value: "beige", label: "Beige", hex: "#F5F5DC" },
  { value: "maroon", label: "Maroon", hex: "#800000" },
  { value: "orange", label: "Orange", hex: "#FF9800" },
  { value: "purple", label: "Purple", hex: "#9C27B0" },
  { value: "navy-blue", label: "Navy Blue", hex: "#000080" },
  { value: "dark-gray", label: "Dark Gray", hex: "#A9A9A9" },
  { value: "light-blue", label: "Light Blue", hex: "#ADD8E6" },
  { value: "teal", label: "Teal", hex: "#009688" },
];

// Updated Color Picker Modal Component
const ColorPickerModal = ({
  isOpen,
  onClose,
  selectedColor,
  onSelectColor,
}) => {
  if (!isOpen) return null;

  return (
    <div
      className="modal-backdrop"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
      }}
      onClick={onClose}
    >
      <div
        className="modal-content bg-base radius-12 shadow-3"
        style={{
          maxWidth: "600px",
          width: "90%",
          maxHeight: "80vh",
          overflow: "auto",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="modal-header border-bottom pb-16 px-24 pt-24">
          <div className="d-flex align-items-center justify-content-between w-100">
            <h6 className="text-primary-light m-0">Select Vehicle Color</h6>
            <button
              onClick={onClose}
              className="btn p-0 hover-bg-transparent"
              type="button"
            >
              <X size={20} className="text-primary-light" />
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <div className="modal-body p-24">
          <div className="grid-container" style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))",
            gap: "16px",
          }}>
            {colorOptions.map((color) => {
              const isSelected = selectedColor === color.value;
              
              return (
                <div
                  key={color.value}
                  onClick={() => {
                    onSelectColor(color);
                    onClose();
                  }}
                  className={`color-option radius-8 p-12 cursor-pointer ${
                    isSelected 
                      ? "border border-width-2-px border-primary-600 bg-primary-50" 
                      : "border border-neutral-300 bg-base hover-bg-neutral-50"
                  }`}
                  style={{
                    transition: "all 0.2s ease",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  {/* Color Swatch */}
                  <div
                    className="radius-8 mb-8"
                    style={{
                      width: "60px",
                      height: "60px",
                      backgroundColor: color.hex,
                      border: color.value === "white" 
                        ? "1px solid var(--neutral-300)" 
                        : "1px solid " + color.hex,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    }}
                  />
                  
                  {/* Color Label */}
                  <span className="text-sm text-primary-light text-center">
                    {color.label}
                  </span>
                  
                  {/* Selected Indicator */}
                  {isSelected && (
                    <div className="mt-8">
                      <div className="bg-primary-600 text-base text-xs radius-4 px-8 py-2">
                        Selected
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Modal Footer */}
        <div className="modal-footer border-top pt-16 px-24 pb-24">
          <div className="d-flex justify-content-end gap-16">
            <button
              type="button"
              onClick={onClose}
              className="btn border border-neutral-300 radius-8 px-20 py-8 text-neutral-700 hover-bg-neutral-100"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => {
                if (selectedColor) {
                  onSelectColor({ value: "", label: "", hex: "" });
                }
                onClose();
              }}
              className="btn border border-neutral-300 radius-8 px-20 py-8 text-neutral-700 hover-bg-neutral-100"
            >
              Clear Selection
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const validationSchema = Yup.object().shape({
  customer_name: Yup.string()
    .required("Name is required")
    .min(2, "Name must be at least 2 characters"),
  email: Yup.string()
    .email("Invalid email")
    .optional()
    .nullable()
    .transform((value) => (value === "" ? null : value)),
  mobile_number: Yup.string()
    .required("Phone number is required")
    .test(
      "is-valid-phone",
      "Invalid phone number for selected country",
      (value) => (value ? isValidPhoneNumber(value) : false)
    ),
  national_id: Yup.string().optional(),
  notes: Yup.string().nullable(),
  is_blacklisted: Yup.boolean().required("Status is required"),
  plate_number: Yup.string().required("Plate number is required"),
  vehicle_type: Yup.string().required("Vehicle type is required"),
  vehicle_make: Yup.string().required("Vehicle make is required"),
  vehicle_model: Yup.string().required("Vehicle model is required"),
  vehicle_color: Yup.string().required("Vehicle color is required"),
  countryId: Yup.string().optional(),
  authorityId: Yup.string().optional(),
});

const EditCustomerProfilePage = () => {
  const location = useLocation();
  const { customer } = location.state || {};
  const customerId = customer.customer_id;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [vehicleMakes, setVehicleMakes] = useState([]);
  const [vehicleModels, setVehicleModels] = useState([]);
  const [selectedMake, setSelectedMake] = useState(null);
  const [isColorModalOpen, setIsColorModalOpen] = useState(false);
  const [loadingStates, setLoadingStates] = useState({
    types: false,
    makes: false,
    models: false,
  });

  const {
    register,
    control,
    setValue,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      customer_name: "",
      email: "",
      mobile_number: "",
      national_id: "",
      notes: "",
      loyalty_code: "",
      is_blacklisted: false,
      plate_number: "",
      vehicle_type: "",
      vehicle_make: "",
      vehicle_model: "",
      vehicle_color: "",
      countryId: "",
      authorityId: "",
    },
  });

  const selectedColorValue = watch("vehicle_color");
  const selectedColorObj = colorOptions.find(
    (c) => c.value === selectedColorValue
  );

  // Fetch Vehicle Types
  useEffect(() => {
    const fetchVehicleTypes = async () => {
      try {
        setLoadingStates((prev) => ({ ...prev, types: true }));
        const response = await apiCall(
          "get",
          "/get-all-vehicle-types",
          { want_pagination: 0, page: 1, per_page: 100 },
          navigate
        );
        if (response?.data?.success && Array.isArray(response.data.data)) {
          setVehicleTypes(response.data.data);
        } else {
          setVehicleTypes([]);
        }
      } catch (error) {
        console.error("Error fetching vehicle types:", error);
        setVehicleTypes([]);
        toast.error("Failed to load vehicle types");
      } finally {
        setLoadingStates((prev) => ({ ...prev, types: false }));
      }
    };
    fetchVehicleTypes();
  }, [navigate]);

  // Fetch Vehicle Makes
  useEffect(() => {
    const fetchVehicleMakes = async () => {
      try {
        setLoadingStates((prev) => ({ ...prev, makes: true }));
        const response = await apiCall(
          "get",
          "/get-all-vehicle-makes",
          { want_pagination: 0, page: 1, per_page: 100 },
          navigate
        );
        const makesData = response?.data?.data?.data || response?.data?.data;
        if (response?.data?.success && Array.isArray(makesData)) {
          setVehicleMakes(makesData);
        } else {
          setVehicleMakes([]);
        }
      } catch (error) {
        console.error("Error fetching vehicle makes:", error);
        setVehicleMakes([]);
        toast.error("Failed to load vehicle makes");
      } finally {
        setLoadingStates((prev) => ({ ...prev, makes: false }));
      }
    };
    fetchVehicleMakes();
  }, [navigate]);

  // Fetch Vehicle Models when Make is selected
  useEffect(() => {
    const fetchVehicleModels = async () => {
      if (!selectedMake) {
        setVehicleModels([]);
        return;
      }
      try {
        setLoadingStates((prev) => ({ ...prev, models: true }));
        const response = await apiCall(
          "get",
          `/get-vehicle-models-by-make/${selectedMake}`,
          { want_pagination: 0, page: 1, per_page: 100 },
          navigate
        );
        const modelsData = response?.data?.data?.data || response?.data?.data;
        if (response?.data?.success && Array.isArray(modelsData)) {
          setVehicleModels(modelsData);
        } else {
          setVehicleModels([]);
        }
      } catch (error) {
        console.error("Error fetching vehicle models:", error);
        setVehicleModels([]);
        toast.error("Failed to load vehicle models");
      } finally {
        setLoadingStates((prev) => ({ ...prev, models: false }));
      }
    };
    fetchVehicleModels();
  }, [selectedMake, navigate]);

  useEffect(() => {
    const fetchCustomer = async () => {
      try {
        const response = await customerApi.getCustomerProfileById(customerId);
        const data = response.data || {};
        setValue("customer_name", data.customer_name || "");
        setValue("email", data.email || "");
        setValue("mobile_number", data.mobile_number || "");
        setValue("national_id", data.national_id || "");
        setValue("notes", data.notes || "");
        setValue("loyalty_code", data.loyalty_code || "");
        setValue("is_blacklisted", data.is_blacklisted || false);
        setValue("plate_number", data.plate_number || "");
        setValue("vehicle_type", data.type_id || "");
        setValue("vehicle_make", data.make_id || "");
        setValue("vehicle_model", data.model_id || "");
        setValue("vehicle_color", data.vehicle_color || "");
        setValue("countryId", data.registration_country_id || "");
        setValue("authorityId", data.plate_authority_id || "");

        if (data.make_id) {
          setSelectedMake(data.make_id);
        }
      } catch (error) {
        toast.error(error.message || "Failed to load customer data");
      } finally {
        setLoading(false);
      }
    };

    fetchCustomer();
  }, [customerId, setValue]);

  const onSubmit = async (data) => {
    setSubmitting(true);
    try {
      const { loyalty_code, ...payload } = data;
      const finalPayload = {
        ...payload,
        vehicle_type: parseInt(data.vehicle_type),
        vehicle_make: parseInt(data.vehicle_make),
        vehicle_model: parseInt(data.vehicle_model),
        registration_country_id: data.countryId
          ? parseInt(data.countryId)
          : undefined,
        plate_authority_id: data.authorityId
          ? parseInt(data.authorityId)
          : undefined,
      };

      await customerApi.updateCustomerProfile(customerId, finalPayload);
      toast.success("Customer profile updated successfully");
      navigate("/customer-profiles");
    } catch (error) {
      toast.error(error.message || "Failed to update customer profile");
    } finally {
      setSubmitting(false);
    }
  };

  const statusOptions = [
    { label: "Active", value: false },
    { label: "Inactive", value: true },
  ];

  // Transform options for CustomSelect
  const vehicleTypeOptions = Array.isArray(vehicleTypes)
    ? vehicleTypes.map((type) => ({
        value: type.type_id,
        label: type.type_name,
      }))
    : [];

  const vehicleMakeOptions = Array.isArray(vehicleMakes)
    ? vehicleMakes.map((make) => ({
        value: make.make_id,
        label: make.make_name,
      }))
    : [];

  const vehicleModelOptions = Array.isArray(vehicleModels)
    ? vehicleModels.map((model) => ({
        value: model.model_id,
        label: model.model_name,
      }))
    : [];

  if (loading) {
    return <LoadingComponent />;
  }

  return (
    <>
      <Breadcrumb title="Edit Customer" />
      <div className="card h-100 p-0 radius-12">
        <div className="card-body p-24">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="row g-20">
              {/* Customer Name */}
              <div className="col-md-6 mb-20">
                <label className="form-label text-primary-light">Name *</label>
                <input
                  {...register("customer_name")}
                  className={`form-control radius-8 ${
                    errors.customer_name ? "border-danger-500" : ""
                  }`}
                  placeholder="Enter Name"
                />
                {errors.customer_name && (
                  <span className="text-danger-500 text-sm mt-4 d-block">
                    {errors.customer_name.message}
                  </span>
                )}
              </div>

              {/* Email */}
              <div className="col-md-6 mb-20">
                <label className="form-label text-primary-light">Email</label>
                <input
                  {...register("email")}
                  type="email"
                  className={`form-control radius-8 ${
                    errors.email ? "border-danger-500" : ""
                  }`}
                  placeholder="Enter Email (Optional)"
                />
                {errors.email && (
                  <span className="text-danger-500 text-sm mt-4 d-block">
                    {errors.email.message}
                  </span>
                )}
              </div>

              {/* Phone Number */}
              <div className="col-md-6 mb-20">
                <CustomPhoneInput
                  name="mobile_number"
                  control={control}
                  errors={errors}
                />
              </div>

              {/* Status */}
              <div className="col-md-6 mb-20">
                <label className="form-label text-primary-light">Status *</label>
                <Controller
                  name="is_blacklisted"
                  control={control}
                  render={({ field }) => (
                    <CustomSelect
                      name="is_blacklisted"
                      value={statusOptions.find(opt => opt.value === field.value) || null}
                      onChange={(selected) => field.onChange(selected?.value)}
                      options={statusOptions}
                      placeholder="Select Status"
                      isSearchable={false}
                      isClearable={false}
                      classNamePrefix="custom-select"
                    />
                  )}
                />
                {errors.is_blacklisted && (
                  <span className="text-danger-500 text-sm mt-4 d-block">
                    {errors.is_blacklisted.message}
                  </span>
                )}
              </div>

              {/* National ID */}
              <div className="col-md-6 mb-20">
                <label className="form-label text-primary-light">National ID</label>
                <input
                  {...register("national_id")}
                  className="form-control radius-8"
                  placeholder="Enter National ID (Optional)"
                />
              </div>

              {/* Loyalty Code */}
              <div className="col-md-6 mb-20">
                <label className="form-label text-primary-light">Loyalty Code</label>
                <input
                  {...register("loyalty_code")}
                  className="form-control radius-8 bg-neutral-100"
                  placeholder="Loyalty Code"
                  readOnly
                  disabled
                />
              </div>

              {/* Plate Number Component */}
              <div className="col-md-12 mb-20">
                <label className="form-label text-primary-light">Plate Number *</label>
                <Controller
                  name="plate_number"
                  control={control}
                  render={({ field }) => (
                    <PlateNumberComponent
                      field={{ ...field, name: "plate_number", required: true }}
                      formData={watch()}
                      setFormData={(updater) => {
                        const newData =
                          typeof updater === "function"
                            ? updater(watch())
                            : updater;
                        Object.keys(newData).forEach((key) => {
                          setValue(key, newData[key]);
                        });
                      }}
                    />
                  )}
                />
                {errors.plate_number && (
                  <div className="text-danger-500 text-sm mt-4">
                    {errors.plate_number.message}
                  </div>
                )}
              </div>

              {/* Vehicle Type */}
              <div className="col-md-6 mb-20">
                <label className="form-label text-primary-light">Vehicle Type *</label>
                <Controller
                  name="vehicle_type"
                  control={control}
                  render={({ field }) => (
                    <CustomSelect
                      {...field}
                      options={vehicleTypeOptions}
                      placeholder="Select Vehicle Type"
                      isSearchable={true}
                      isClearable={true}
                      isLoading={loadingStates.types}
                      value={
                        vehicleTypeOptions.find(
                          (opt) => opt.value === field.value
                        ) || null
                      }
                      onChange={(selected) =>
                        field.onChange(selected ? selected.value : "")
                      }
                    />
                  )}
                />
                {errors.vehicle_type && (
                  <div className="text-danger-500 text-sm mt-4">
                    {errors.vehicle_type.message}
                  </div>
                )}
              </div>

              {/* Vehicle Make */}
              <div className="col-md-6 mb-20">
                <label className="form-label text-primary-light">Vehicle Make *</label>
                <Controller
                  name="vehicle_make"
                  control={control}
                  render={({ field }) => (
                    <CustomSelect
                      {...field}
                      options={vehicleMakeOptions}
                      placeholder="Select Vehicle Make"
                      isSearchable={true}
                      isClearable={true}
                      isLoading={loadingStates.makes}
                      value={
                        vehicleMakeOptions.find(
                          (opt) => opt.value === field.value
                        ) || null
                      }
                      onChange={(selected) => {
                        field.onChange(selected ? selected.value : "");
                        setSelectedMake(selected ? selected.value : null);
                        setValue("vehicle_model", "");
                      }}
                    />
                  )}
                />
                {errors.vehicle_make && (
                  <div className="text-danger-500 text-sm mt-4">
                    {errors.vehicle_make.message}
                  </div>
                )}
              </div>

              {/* Vehicle Model */}
              <div className="col-md-6 mb-20">
                <label className="form-label text-primary-light">Vehicle Model *</label>
                <Controller
                  name="vehicle_model"
                  control={control}
                  render={({ field }) => (
                    <CustomSelect
                      {...field}
                      options={vehicleModelOptions}
                      placeholder={
                        selectedMake
                          ? "Select Vehicle Model"
                          : "Select Make First"
                      }
                      isSearchable={true}
                      isClearable={true}
                      isDisabled={!selectedMake}
                      isLoading={loadingStates.models}
                      value={
                        vehicleModelOptions.find(
                          (opt) => opt.value === field.value
                        ) || null
                      }
                      onChange={(selected) =>
                        field.onChange(selected ? selected.value : "")
                      }
                    />
                  )}
                />
                {errors.vehicle_model && (
                  <div className="text-danger-500 text-sm mt-4">
                    {errors.vehicle_model.message}
                  </div>
                )}
              </div>

              {/* Vehicle Color - Updated to match style.css */}
              <div className="col-md-6 mb-20">
                <label className="form-label text-primary-light">Vehicle Color *</label>
                <div
                  onClick={() => setIsColorModalOpen(true)}
                  className={`radius-8 border ${
                    errors.vehicle_color 
                      ? "border-danger-500" 
                      : "border-neutral-300"
                  } cursor-pointer hover-bg-neutral-50 transition-2`}
                  style={{
                    padding: "11px 16px",
                    minHeight: "46px",
                    display: "flex",
                    alignItems: "center",
                    backgroundColor: "var(--white)",
                  }}
                >
                  {selectedColorObj ? (
                    <div className="d-flex align-items-center gap-10">
                      <div
                        className="radius-4"
                        style={{
                          width: "24px",
                          height: "24px",
                          backgroundColor: selectedColorObj.hex,
                          border: selectedColorObj.value === "white" 
                            ? "1px solid var(--neutral-300)" 
                            : "1px solid " + selectedColorObj.hex,
                        }}
                      />
                      <span className="text-primary-light">{selectedColorObj.label}</span>
                    </div>
                  ) : (
                    <span className="text-neutral-500">Select Vehicle Color</span>
                  )}
                  <div className="ms-auto">
                    <div className="text-xs text-neutral-400">Click to select</div>
                  </div>
                </div>
                {errors.vehicle_color && (
                  <div className="text-danger-500 text-sm mt-4">
                    {errors.vehicle_color.message}
                  </div>
                )}
              </div>

              {/* Color Picker Modal */}
              <ColorPickerModal
                isOpen={isColorModalOpen}
                onClose={() => setIsColorModalOpen(false)}
                selectedColor={selectedColorValue}
                onSelectColor={(color) =>
                  setValue("vehicle_color", color.value)
                }
              />

              {/* Notes */}
              <div className="col-md-12 mb-20">
                <label className="form-label text-primary-light">Notes</label>
                <textarea
                  {...register("notes")}
                  className="form-control radius-8"
                  placeholder="Enter Notes"
                  rows="4"
                />
              </div>
            </div>

            <div className="d-flex justify-content-center gap-16 mt-32">
              <CancelButtonForm />
              <UpdateButtonForm disabled={submitting} />
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default EditCustomerProfilePage;